// Settings › App information (wave 3, W2_Portal_Settings look): the app and
// company details and the store / social links, one setting per row, and a
// save bar that counts unsaved changes.
//
// Save writes exactly what the old Submit wrote: the WHOLE settings object
// (with these fields edited) through editSettings, only with critical edits
// on, and not while the referral bonus is empty (as before).
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import { api } from 'common';
import CircularLoading from '../components/CircularLoading';
import { showToast, confirmDialog, InlineMessage } from '../components/feedback';
import { tokens as c } from '../theme';
import { SectionCard, SettingRow, SaveBar, IconCircle, calmCaps } from '../components/modern';

// The settings saved here survive switching Settings tabs for the rest of
// the session while the store still holds the settings they started from
// (a save now updates the store too - common editSettings - so this only
// covers the moment before the store's new copy arrives).
let savedCache = null; // { source, data }

const FIELDS = [
  { key: 'appName', group: 'info', label: ['settings_app_name', 'App name'] },
  { key: 'CompanyName', group: 'info', label: ['settings_company_name', 'Company name'] },
  { key: 'CompanyAddress', group: 'info', label: ['CompanyAddress', 'Company address'] },
  { key: 'CompanyWebsite', group: 'info', label: ['settings_company_website', 'Company website'], inputMode: 'url' },
  { key: 'contact_email', group: 'info', label: ['contact_email', 'Contact email'], inputMode: 'email' },
  { key: 'CompanyPhone', group: 'info', label: ['company_phone', 'Company phone'], inputMode: 'tel' },
  { key: 'CompanyTerms', group: 'links', label: ['terms', 'Privacy policy'], inputMode: 'url' },
  { key: 'CompanyTermCondition', group: 'links', label: ['term_condition', 'Terms & conditions'], inputMode: 'url' },
  { key: 'FacebookHandle', group: 'links', label: ['FacebookHandle', 'Facebook page link'], inputMode: 'url' },
  { key: 'TwitterHandle', group: 'links', label: ['TwitterHandle', 'Twitter page link'], inputMode: 'url' },
  { key: 'InstagramHandle', group: 'links', label: ['InstagramHandle', 'Instagram page link'], inputMode: 'url' },
  { key: 'AppleStoreLink', group: 'links', label: ['AppleStoreLink', 'Apple Store link'], inputMode: 'url' },
  { key: 'PlayStoreLink', group: 'links', label: ['PlayStoreLink', 'Play Store link'], inputMode: 'url' },
];

// Wide screens (lg and up): the app details and the links sit side by side
// in two columns; below lg they stack as before.
const PAIR_SX = {
  display: 'grid',
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' },
  columnGap: 4,
  alignItems: 'start',
};

const shown = (v) => (v === undefined || v === null ? '' : String(v));
const startFrom = (source) => (savedCache && source && savedCache.source === source ? savedCache.data : source || null);

function GroupTitle({ icon, hue, title }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pt: 2.5, pb: 0.5 }}>
      <IconCircle icon={icon} hue={hue} size={30} />
      <Typography component="h3" sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1.4, color: c.INK }}>
        {title}
      </Typography>
    </Box>
  );
}

const AppInformation = () => {
  const { t, i18n } = useTranslation();
  const { editSettings, clearSettingsViewError } = api;
  const settingsdata = useSelector((state) => state.settingsdata);
  const dispatch = useDispatch();
  const source = settingsdata.settings;

  const [base, setBase] = useState(() => startFrom(source));
  const [data, setData] = useState(() => startFrom(source));
  const [clicked, setClicked] = useState(false);
  const [saving, setSaving] = useState(false);
  const sourceRef = useRef(source);
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (source && source !== sourceRef.current) {
      sourceRef.current = source;
      setBase(startFrom(source));
      setData(startFrom(source));
    }
  }, [source]);

  const tx = (pair) => calmCaps(String(t(pair[0], { defaultValue: pair[1] })).replace(/\s*:\s*$/, ''));

  const changedKeys = useMemo(
    () => (data && base ? FIELDS.filter((f) => shown(data[f.key]) !== shown(base[f.key])).map((f) => f.key) : []),
    [data, base]
  );

  const handleTextChange = (key, value) => setData((d) => ({ ...d, [key]: value }));

  const discard = async () => {
    if (!changedKeys.length || saving) return;
    const ok = await confirmDialog({
      title:
        changedKeys.length === 1
          ? t('settings_discard_title_one', { defaultValue: 'Discard 1 unsaved change?' })
          : t('settings_discard_title_many', { n: changedKeys.length, defaultValue: 'Discard {{n}} unsaved changes?' }),
      body: t('settings_discard_body', { defaultValue: 'The settings go back to how they were when you last saved.' }),
      confirmLabel: t('settings_discard', { defaultValue: 'Discard' }),
      destructive: true,
    });
    if (ok && aliveRef.current) setData(base);
  };

  const handleSubmit = async () => {
    if (!data || saving) return;
    if (data.AllowCriticalEditsAdmin) {
      if (data.bonus === '') {
        showToast.error(t('proper_bonus'));
      } else {
        const payload = data;
        setClicked(true);
        setSaving(true);
        try {
          await dispatch(editSettings(payload));
          savedCache = { source: sourceRef.current, data: payload };
          if (!aliveRef.current) return;
          setBase(payload);
          showToast.success(t('settings_updated', { defaultValue: 'Settings updated' }));
        } catch (e) {
          if (aliveRef.current) showToast.error(t('update_failed', { defaultValue: 'Update failed' }));
        } finally {
          if (aliveRef.current) setSaving(false);
        }
      }
    } else {
      showToast.warning(t('demo_mode'));
    }
  };

  const handleClose = () => {
    setClicked(false);
    dispatch(clearSettingsViewError());
  };

  if (!data) return <CircularLoading inline />;

  const renderRow = (f) => {
    const id = `appinfo-${f.key}`;
    return (
      <SettingRow
        key={f.key}
        id={id}
        label={tx(f.label)}
        changed={changedKeys.includes(f.key)}
        control={
          <TextField
            id={id}
            size="small"
            value={shown(data[f.key])}
            onChange={(e) => handleTextChange(f.key, e.target.value)}
            inputProps={{ inputMode: f.inputMode, autoComplete: 'off', spellCheck: f.inputMode ? false : undefined }}
            sx={{ width: { xs: '100%', sm: 420 } }}
          />
        }
      />
    );
  };

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <SectionCard
        id="appinfo"
        icon={<InfoOutlinedIcon />}
        hue="info"
        title={tx(['app_info_title', 'App information'])}
        sx={{ display: 'flex', flexDirection: 'column', pb: 0 }}
      >
        {settingsdata.error && settingsdata.error.flag && clicked ? (
          <InlineMessage kind="error" onClose={handleClose} sx={{ mt: 1.5 }}>
            {t('update_failed', { defaultValue: 'Update failed' })}
          </InlineMessage>
        ) : null}
        <Box sx={PAIR_SX}>
          <Box sx={{ minWidth: 0 }}>
            <GroupTitle icon={<BusinessOutlinedIcon />} hue="accent" title={tx(['app_info', 'App information'])} />
            <Box>{FIELDS.filter((f) => f.group === 'info').map(renderRow)}</Box>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <GroupTitle icon={<LinkRoundedIcon />} hue="info" title={tx(['app_link', 'App links'])} />
            <Box sx={{ pb: 1 }}>{FIELDS.filter((f) => f.group === 'links').map(renderRow)}</Box>
          </Box>
        </Box>
        <SaveBar
          changedCount={changedKeys.length}
          onDiscard={discard}
          onSave={handleSubmit}
          saving={saving}
          sx={{ mx: { xs: -2, md: -2.5 }, px: { xs: 2, md: 2.5 } }}
        />
      </SectionCard>
    </Box>
  );
};

export default AppInformation;
